import {BehaviorSubject, ReplaySubject} from 'rxjs';
import {filter, map, shareReplay} from 'rxjs/operators';
import {arr2Map} from '@/helper/structure-covert';
import type {
  CurrencyEntity,
  ContractPair,
  SpotPair,
  ContractRiskLimit,
  Leverage,
  MarginCurrencyEntity,
  MarginPair,
  MarginRiskLevels,
  UtaState,
  LeverageMargin,
} from '@/types';

// 数据量：300+
export const currencies$ = new ReplaySubject<CurrencyEntity[]>(1);
// currency config inAssetsDisplay为1
export const inAssetCurrencies$ = new ReplaySubject<string[]>(1);
// currency config inAssetsDisplay为0
export const unAssetCurrencies$ = new ReplaySubject<string[]>(1);

export const contracts$ = new ReplaySubject<ContractPair[]>(1);
export const spots$ = new ReplaySubject<SpotPair[]>(1);
export const riskLimits$ = new ReplaySubject<ContractRiskLimit[]>(1);
// 数据量：25
export const leverages$ = new ReplaySubject<Leverage[]>(1);
// 数据量：56
export const marginCurrencies$ = new ReplaySubject<MarginCurrencyEntity[]>(1);
// 数据量：0
export const marginPairs$ = new ReplaySubject<MarginPair[]>(1);
// 数据量：1
export const marginRiskLevels$ = new ReplaySubject<MarginRiskLevels>(1);

export const utaState$ = new BehaviorSubject<UtaState>(createInitUtaState());

// 浮盈加仓灰度, 已全面放开支持
export const riskEngineMode$ = new BehaviorSubject<Boolean>(true);
// 反向合约浮盈加仓灰度
export const coinMarginedContractGray$ = new BehaviorSubject<Boolean>(true);

// 合约默认交易费率
export const contractDefaultTakerFeeRateEr$ = new BehaviorSubject<number>(0.0006);

export const leverageMargins$ = new ReplaySubject<LeverageMargin[]>(1);
export const leverageMarginsCache$ = leverageMargins$.pipe(
  map((contracts) =>
    contracts.reduce((res, cur) => res.set('id_' + cur['index_id'], cur), new Map<string, LeverageMargin>())
  )
);

// 原数据，无放大
export const spots2$ = spots$.pipe(
  map((o) =>
    o.map((o) => ({
      ...o,
      baseQtyScale: 0,
      baseQtyFactor: 1,
      quoteQtyScale: 0,
      quoteQtyFactor: 1,
    }))
  )
);

export const riskLimitCache$ = riskLimits$.pipe(
  map((riskLimits) => arr2Map(riskLimits, 'symbol')),
  shareReplay(1)
);

export const currenciesCache$ = currencies$.pipe(
  map((currencies) => arr2Map(currencies, 'symbol')),
  shareReplay(1)
);

export const currenciesCurrencyCache$ = currencies$.pipe(
  map((currencies) => arr2Map(currencies, 'currency')),
  shareReplay(1)
);

export const ptConfig$ = currenciesCurrencyCache$.pipe(
  filter((cache) => cache.has('PT')),
  map((cache) => cache.get('PT')),
  shareReplay(1)
);

export const contractsCache$ = contracts$.pipe(
  map((contracts) => arr2Map(contracts, 'symbol')),
  shareReplay(1)
);

export const contractsIndexSymbolCache$ = contracts$.pipe(
  map((contracts) => arr2Map(contracts, 'indexSymbol')),
  shareReplay(1)
);

export const contractsMarkSymbolCache$ = contracts$.pipe(
  map((contracts) => arr2Map(contracts, 'markSymbol')),
  shareReplay(1)
);

export const spotsCache$ = spots$.pipe(
  map((spots) => arr2Map(spots, 'symbol')),
  shareReplay(1)
);

export const marginCurrenciesCurrencyCache$ = marginCurrencies$.pipe(
  map((currencies) => arr2Map(currencies, 'currency')),
  shareReplay(1)
);

export const contractSettleCurrencies$ = contracts$.pipe(
  map((contracts): string[] =>
    contracts.reduce((p, item) => (p.includes(item.settleCurrency) ? p : [...p, item.settleCurrency]), [])
  )
);

export const contractAccountCurrencies$ = contractSettleCurrencies$.pipe(map((o) => [...o, 'PT']));

currencies$.subscribe((currencies: CurrencyEntity[]) => {
  inAssetCurrencies$.next(
    currencies.filter((o: CurrencyEntity) => !!o.inAssetsDisplay).map((o: CurrencyEntity) => o.symbol)
  );
  unAssetCurrencies$.next(
    currencies.filter((o: CurrencyEntity) => !o.inAssetsDisplay).map((o: CurrencyEntity) => o.symbol)
  );
});

function createInitUtaState(): UtaState {
  return {spot: 0};
}
